import React, { useEffect, useState } from "react";
// react admin
import { useGetIdentity, useGetList, useUpdate } from "react-admin";
// mui
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Skeleton,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
// loader
import { Loader } from "../../Loader";

export const WinningScreenSettings = () => {
  const { data: identity, isPending } = useGetIdentity();
  const [update, { isLoading }] = useUpdate();
  const { data: gameSetting, refetch } = useGetList("GameSettings", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "createdAt", order: "DESC" },
  });

  const [settings, setSettings] = useState({
    winningScreenRandomizeEnabled: false,
    winningScreenRandomizeThreshold: 5,
    winningScreenRandomizeMin: 2,
    winningScreenRandomizeMax: 5,
    dummyWinningsEnabled: false,
    dummyWinningsMinAmount: 25,
    dummyWinningsMaxAmount: 200,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [validationError, setValidationError] = useState("");

  const isSuperUser = identity?.userRoleName === "Super-User";

  useEffect(() => {
    if (gameSetting?.length) {
      const [first] = gameSetting;
      const newSettings = {
        id: first.id,
        winningScreenRandomizeEnabled: Boolean(
          first.winningScreenRandomizeEnabled
        ),
        winningScreenRandomizeThreshold:
          first.winningScreenRandomizeThreshold ?? 5,
        winningScreenRandomizeMin: first.winningScreenRandomizeMin ?? 2,
        winningScreenRandomizeMax: first.winningScreenRandomizeMax ?? 5,
        dummyWinningsEnabled: Boolean(first.dummyWinningsEnabled),
        dummyWinningsMinAmount: first.dummyWinningsMinAmount ?? 25,
        dummyWinningsMaxAmount: first.dummyWinningsMaxAmount ?? 200,
      };
      setSettings(newSettings);
    }
  }, [gameSetting]);

  const handleOpenDialog = () => {
    setEditValues({ ...settings });
    setValidationError("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleEditToggle = (event) => {
    setEditValues((prev) => ({
      ...prev,
      winningScreenRandomizeEnabled: event.target.checked,
    }));
  };

  const handleEditChange = (field) => (event) => {
    const value = event.target.value;
    // For Min and Max fields (both randomize and dummy), only allow integers (no decimals)
    if (
      field === "winningScreenRandomizeMin" || 
      field === "winningScreenRandomizeMax" ||
      field === "dummyWinningsMinAmount" ||
      field === "dummyWinningsMaxAmount"
    ) {
      setEditValues((prev) => ({
        ...prev,
        [field]: value === "" ? "" : parseInt(value, 10) || 0,
      }));
    } else {
      setEditValues((prev) => ({
        ...prev,
        [field]: value === "" ? "" : Number(value),
      }));
    }
  };

  // Block decimal input for Min/Max fields
  const handleKeyDown = (event) => {
    if (event.key === "." || event.key === ",") {
      event.preventDefault();
    }
  };

  const handleSave = () => {
    const min = Number(editValues.winningScreenRandomizeMin) || 0;
    const max = Number(editValues.winningScreenRandomizeMax) || 0;
    const dummyMin = Number(editValues.dummyWinningsMinAmount) || 0;
    const dummyMax = Number(editValues.dummyWinningsMaxAmount) || 0;
    
    // Validation: Max must be >= Min for randomize settings
    if (max < min) {
      setValidationError("Randomize Max value cannot be less than Min value");
      return;
    }
    
    // Validation: Max must be >= Min for dummy winnings
    if (dummyMax < dummyMin) {
      setValidationError("Dummy Winnings Max amount cannot be less than Min amount");
      return;
    }
    
    setValidationError("");
    
    const dataToSave = {
      winningScreenRandomizeEnabled: editValues.winningScreenRandomizeEnabled,
      winningScreenRandomizeThreshold:
        Number(editValues.winningScreenRandomizeThreshold) || 0,
      winningScreenRandomizeMin: min,
      winningScreenRandomizeMax: max,
      dummyWinningsEnabled: editValues.dummyWinningsEnabled,
      dummyWinningsMinAmount: dummyMin,
      dummyWinningsMaxAmount: dummyMax,
    };

    update(
      "GameSettings",
      {
        id: settings.id,
        data: dataToSave,
      },
      {
        onSuccess: () => {
          setSettings((prev) => ({ ...prev, ...dataToSave }));
          setDialogOpen(false);
          refetch();
        },
      }
    );
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "#555" },
      "&:hover fieldset": { borderColor: "#888" },
      "&.Mui-focused fieldset": { borderColor: "#FFD700" },
      "&.Mui-disabled": {
        "& fieldset": { borderColor: "#333" },
        color: "#666",
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#666",
    },
    "& .MuiInputLabel-root": {
      color: "#999",
    },
  };

  return (
    <React.Fragment>
      {isLoading && <Loader />}
      {isPending ? (
        <Card
          variant="outlined"
          sx={{ mt: 2, backgroundColor: "#242424", borderRadius: "8px" }}
        >
          <CardContent>
            <Skeleton
              width="100%"
              height={100}
              sx={{ mt: 1, background: "#fff" }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card
          variant="outlined"
          sx={{ mt: 2, backgroundColor: "#242424", borderRadius: "8px" }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#FFF", fontSize: 18 }}
                >
                  Winning Screen Settings
                </Typography>
                {isSuperUser && (
                  <IconButton
                    size="small"
                    onClick={handleOpenDialog}
                    sx={{ color: "#FFD700" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box
                sx={{
                  backgroundColor: "rgba(255, 215, 0, 0.20)",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <img
                  src="/Assets/Icons/user-account.svg"
                  alt="Settings Icon"
                  style={{ width: 24, height: 24 }}
                />
              </Box>
            </Box>

            {isSuperUser && (
              <Box sx={{ mt: 2 }}>
                {/* Randomize Low Wins Section */}
                <Typography sx={{ color: "#FFD700", fontSize: 15, fontWeight: 600, mb: 1 }}>
                  Randomize Low Wins (Real Winnings)
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#999", fontSize: 14 }}>
                    Status
                  </Typography>
                  <Typography
                    sx={{
                      color: settings.winningScreenRandomizeEnabled
                        ? "#4CAF50"
                        : "#f44336",
                      fontSize: 14,
                    }}
                  >
                    {settings.winningScreenRandomizeEnabled
                      ? "Enabled"
                      : "Disabled"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#999", fontSize: 14 }}>
                    Threshold
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: 14 }}>
                    {settings.winningScreenRandomizeThreshold}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#999", fontSize: 14 }}>
                    Min Add Value
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: 14 }}>
                    {settings.winningScreenRandomizeMin}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography sx={{ color: "#999", fontSize: 14 }}>
                    Max Add Value
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: 14 }}>
                    {settings.winningScreenRandomizeMax}
                  </Typography>
                </Box>

                {/* Dummy Winnings Section */}
                <Box sx={{ borderTop: "1px solid #333", pt: 2, mt: 2 }}>
                  <Typography sx={{ color: "#FFD700", fontSize: 15, fontWeight: 600, mb: 1 }}>
                    Dummy Winnings Display
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#999", fontSize: 14 }}>
                      Status
                    </Typography>
                    <Typography
                      sx={{
                        color: settings.dummyWinningsEnabled
                          ? "#4CAF50"
                          : "#f44336",
                        fontSize: 14,
                      }}
                    >
                      {settings.dummyWinningsEnabled
                        ? "Enabled"
                        : "Disabled"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#999", fontSize: 14 }}>
                      Min Amount (VC)
                    </Typography>
                    <Typography sx={{ color: "#fff", fontSize: 14 }}>
                      {settings.dummyWinningsMinAmount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ color: "#999", fontSize: 14 }}>
                      Max Amount (VC)
                    </Typography>
                    <Typography sx={{ color: "#fff", fontSize: 14 }}>
                      {settings.dummyWinningsMaxAmount}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#666", fontSize: 11, mt: 1, fontStyle: "italic" }}>
                    * 8 dummy VE winnings from Top 10 games (25-200 VC)
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            color: "#fff",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #333" }}>
          Edit Winning Screen Settings
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            {/* Randomize Low Wins Section */}
            <Box sx={{ pb: 2, borderBottom: "1px solid #333" }}>
              <Typography sx={{ color: "#FFD700", fontSize: 16, fontWeight: 600, mb: 2 }}>
                Randomize Low Wins (Real Winnings)
              </Typography>
              
              {/* Toggle */}
              <Box>
                <Typography sx={{ color: "#999", fontSize: 14, mb: 1 }}>
                  Enable Randomization
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editValues.winningScreenRandomizeEnabled || false}
                      onChange={handleEditToggle}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ color: "#999", fontSize: 12 }}>
                      {editValues.winningScreenRandomizeEnabled
                        ? "Enabled"
                        : "Disabled"}
                    </Typography>
                  }
                />
              </Box>

              {/* Threshold */}
              <TextField
                label="Threshold (Apply if win < this value)"
                type="number"
                size="small"
                fullWidth
                value={editValues.winningScreenRandomizeThreshold ?? ""}
                onChange={handleEditChange("winningScreenRandomizeThreshold")}
                disabled={!editValues.winningScreenRandomizeEnabled}
                inputProps={{ min: 0, step: 0.5 }}
                sx={{ ...textFieldStyles, mt: 2 }}
              />

              {/* Min/Max */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Min Add Value"
                  type="number"
                  size="small"
                  fullWidth
                  value={editValues.winningScreenRandomizeMin ?? ""}
                  onChange={handleEditChange("winningScreenRandomizeMin")}
                  onKeyDown={handleKeyDown}
                  disabled={!editValues.winningScreenRandomizeEnabled}
                  inputProps={{ min: 0, step: 1 }}
                  sx={textFieldStyles}
                />
                <TextField
                  label="Max Add Value"
                  type="number"
                  size="small"
                  fullWidth
                  value={editValues.winningScreenRandomizeMax ?? ""}
                  onChange={handleEditChange("winningScreenRandomizeMax")}
                  onKeyDown={handleKeyDown}
                  disabled={!editValues.winningScreenRandomizeEnabled}
                  inputProps={{ min: 0, step: 1 }}
                  sx={textFieldStyles}
                />
              </Stack>
            </Box>

            {/* Dummy Winnings Section */}
            <Box>
              <Typography sx={{ color: "#FFD700", fontSize: 16, fontWeight: 600, mb: 2 }}>
                Dummy Winnings Display
              </Typography>
              
              {/* Toggle */}
              <Box>
                <Typography sx={{ color: "#999", fontSize: 14, mb: 1 }}>
                  Enable Dummy Winnings
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editValues.dummyWinningsEnabled || false}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          dummyWinningsEnabled: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ color: "#999", fontSize: 12 }}>
                      {editValues.dummyWinningsEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                  }
                />
                <Typography sx={{ color: "#666", fontSize: 11, mt: 0.5, fontStyle: "italic" }}>
                  Display 8 dummy VE winnings from Top 10 games by bet amount
                </Typography>
              </Box>

              {/* Min/Max Amount */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Min Amount (VC)"
                  type="number"
                  size="small"
                  fullWidth
                  value={editValues.dummyWinningsMinAmount ?? ""}
                  onChange={handleEditChange("dummyWinningsMinAmount")}
                  onKeyDown={handleKeyDown}
                  disabled={!editValues.dummyWinningsEnabled}
                  inputProps={{ min: 25, max: 200, step: 1 }}
                  sx={textFieldStyles}
                />
                <TextField
                  label="Max Amount (VC)"
                  type="number"
                  size="small"
                  fullWidth
                  value={editValues.dummyWinningsMaxAmount ?? ""}
                  onChange={handleEditChange("dummyWinningsMaxAmount")}
                  onKeyDown={handleKeyDown}
                  disabled={!editValues.dummyWinningsEnabled}
                  inputProps={{ min: 25, max: 200, step: 1 }}
                  sx={textFieldStyles}
                />
              </Stack>
            </Box>

            {/* Validation Error */}
            {validationError && (
              <Typography sx={{ color: "#f44336", fontSize: 12 }}>
                {validationError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #333", p: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: "#999" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#FFD700",
              color: "#000",
              "&:hover": { backgroundColor: "#e6c200" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
